import { S } from '@mobily/ts-belt';
import classNames from 'classnames';
import { Stack, Text } from '@stitch-fix/mode-react';
import { AnimatePresence, m } from 'framer-motion';
import { GetStartedButton } from '../GetStartedButton';
import { buttonColorToButtonVariant } from '../ModuleFooter/ModuleFooter';
import { GalleryHeroProps } from './GalleryHero';
import { JsonRteRenderer } from '../JsonRteRenderer';
import { isEmpty } from '../JsonRteRenderer/JsonRteRenderer';
import styles from './styles.module.scss';

/**
 * ContentItem component with framer-motion animations for text rotation.
 *
 * Note: This component expects to be rendered within a MotionConfig provider
 * (e.g., ModularPageWrapper) to ensure animations respect user accessibility
 * preferences for reduced motion.
 */

export type ContentItemProps = {
  slideIndex: number;
  titleTransition?: 'slide-up' | 'fade' | 'none';
} & Pick<GalleryHeroProps, 'items' | 'buttonColor' | 'titleDisplay'>;

// If the current slide does not have a major title, we recursively check for the
// slide before it, until we find one
const getPopulatedTitle = (items: GalleryHeroProps['items'], slideIndex: number): GalleryHeroProps['items'][number] => {
  const slide = items[slideIndex];
  if (slide.majorTitle === '' && slideIndex > 0) {
    return getPopulatedTitle(items, slideIndex - 1);
  }
  return slide;
};
export const ContentItem = ({
  items,
  slideIndex,
  buttonColor,
  titleTransition = 'slide-up',
  titleDisplay = 'minor-major'
}: ContentItemProps) => {
  const {
    majorTitle
  } = getPopulatedTitle(items, slideIndex);
  const slide = items[slideIndex];
  const shouldDisplayCTAButton = S.isNotEmpty(slide.cta.text) && S.isNotEmpty(slide.cta.url);
  const label = items.length > 0 ? `${items[0].minorTitle} ${items[0].majorTitle}` : '';
  const getAnimation = () => {
    if (titleTransition === 'slide-up') {
      return {
        initial: {
          y: 60
        },
        animate: {
          y: 0
        },
        exit: {
          y: -60
        }
      };
    }
    if (titleTransition === 'fade') {
      return {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        exit: {
          opacity: 0
        }
      };
    }
    return {};
  };
  const minorTextSetting = titleDisplay === 'title-xlarge' ? 'title-xlarge' : 'display-medium';
  const majorTextSetting = titleDisplay === 'title-xlarge' ? 'title-xlarge' : 'display-xlarge';
  return <>
      <h1 className={styles.title} aria-label={label}>
        <Text setting={minorTextSetting} as="span" color="blue-45">
          {slide.minorTitle}
        </Text>{' '}
        <div className={classNames(styles.rotator, {
        [styles.slideUp]: titleTransition === 'slide-up'
      })}>
          <AnimatePresence initial={false}>
            <m.div key={majorTitle} transition={{
            duration: 0.5
          }} {...getAnimation()}>
              <Text setting={majorTextSetting} as="span" color="blue-45">
                {titleDisplay === 'title-xlarge' ? majorTitle : <strong>{majorTitle}</strong>}
              </Text>
            </m.div>
          </AnimatePresence>
        </div>
      </h1>
      <Stack spacing={1}>
        {slide.subtitle && !isEmpty(slide.subtitle.children) && <JsonRteRenderer data={slide.subtitle} options={{
        textProps: {
          p: {
            color: 'blue-45',
            spacingBottom: false
          },
          li: {
            color: 'blue-45',
            spacingBottom: false
          }
        }
      }} />}

        {slide.CtaButton || shouldDisplayCTAButton && <GetStartedButton copy={slide.cta.text} href={slide.cta.url} eventLocation="hero" variant={buttonColorToButtonVariant(buttonColor)} />}
      </Stack>
    </>;
};